import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';

import { getDefaultLanguageByRegion } from '../utils/defaultLanguageByRegion.ts';
import { getJourneyAvailability, journeyConfigByJourneyId, type JourneyId } from '../utils/journeyAvailability.ts';
import logger from '../utils/logger.ts';
import { resolveQueryRegion } from '../utils/resolveQueryRegion.ts';
import { useIsMobile } from '../utils/useIsMobile.ts';

const JourneyConfigSchema = z.object({
  description: z.string(),
  nonProdOnly: z.boolean().optional(),
  onboardingRegionQueryParam: z.string().optional(),
  lang: z.string().optional(),
  region: z.string(),
  platform: z.string().nullable(),
  isRegionUnknown: z.boolean().optional(), // derived field, not expected in raw config
  default_platform: z.string().nullable(),
  pegaStartingFields: z.object({
    MultiAsset: z.boolean(),
    Product: z.string().nullable(),
    IAID: z.string().nullable(),
    OnboardingRegion: z.string().nullable(),
    TenantID: z.string(),
    Platform: z.string().nullable(),
    Context: z.string(),
    Domain: z.string(),
    CustomerType: z.string().nullable(),
    IsFromPlatform: z.boolean().nullable(),
    Channel: z.string(),
    CaseID: z.string().optional(),
  }),
});

type JourneyConfigType = z.infer<typeof JourneyConfigSchema>;

function loadJourneyConfig(journeyId: string, query?: URLSearchParams): JourneyConfigType {
  const availability = getJourneyAvailability(journeyId, query);
  if (!availability.allowed) {
    throw new Error(availability.reason ?? `Journey id not supported: ${journeyId}`);
  }

  const rawConfig = journeyConfigByJourneyId[journeyId as JourneyId];
  if (!rawConfig) {
    throw new Error(`Journey id not supported: ${journeyId}`);
  }

  const parsedConfig = JourneyConfigSchema.safeParse(rawConfig);
  if (!parsedConfig.success) {
    throw new Error(`Invalid journey config for "${journeyId}": ${parsedConfig.error.message}`);
  }

  return parsedConfig.data;
}

const validProducts = ['CFD', 'Equity', 'SBP', null] as const;

function isValidProduct(product: string | null): boolean {
  if (!product) return false;
  return validProducts.some((valid) => valid?.toUpperCase() === product?.toUpperCase());
}

// iaid, product, multiasset, embedOneTimeToken embedded

interface MergedConfig extends JourneyConfigType {
  lang: string;
  embedded: boolean;
  journeyId: string | null;
  embedOneTimeToken: string | null;
  product: string | null;
  iaid: string | null;
  multiasset: boolean | null;
  accountId: string | null;
}

const MergedConfigContext = React.createContext<MergedConfig | undefined>(undefined);

function MergedConfigProvider({ children, journeyId }: { children: React.ReactNode; journeyId: string }) {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location]);
  const journeyData = loadJourneyConfig(journeyId, query);
  const isMobile = useIsMobile();

  const mergedConfig: MergedConfig | undefined = useMemo(() => {
    if (!journeyData) return undefined;

    const urlProduct = query.get('product');
    const defaultProduct = journeyData.pegaStartingFields?.Product;
    const product = isValidProduct(urlProduct) ? urlProduct : defaultProduct;

    if (urlProduct && !isValidProduct(urlProduct)) {
      logger.error(
        `Invalid product value "${urlProduct}" provided in URL. Falling back to default: "${defaultProduct}"`
      );
    }

    const isFromPlatform = query.get('isFromPlatform') === 'true';

    // Intent parameter from mobile URL to decide which Context to send
    const urlIntent = query.get('i');
    const context =
      urlIntent === 'demo' ? 'Demo' : urlIntent === 'live' ? 'Live' : journeyData.pegaStartingFields.Context;

    const configOnboardingRegion = journeyData.pegaStartingFields.OnboardingRegion;
    const obrParam = journeyData.onboardingRegionQueryParam ?? 'obr';
    const queryRegion = resolveQueryRegion(query, obrParam);
    const hasConfigRegion = Boolean(journeyData.region);
    const resolvedRegion = journeyData.region || queryRegion || '';
    const resolvedOnboardingRegion = hasConfigRegion
      ? (configOnboardingRegion ?? queryRegion ?? null)
      : (queryRegion ?? configOnboardingRegion ?? null);

    return {
      embedded: query.get('embedded') === 'true',
      product: product,
      embedOneTimeToken: query.get('t'),
      iaid: query.get('iaid'),
      accountId: query.get('accountId'),
      description: journeyData.description,
      journeyId: journeyId,
      platform: journeyData.default_platform,
      pegaStartingFields: {
        ...journeyData.pegaStartingFields,
        OnboardingRegion: resolvedOnboardingRegion,
        Context: context,
        ...(journeyData.pegaStartingFields.Channel === 'Web/Mobile' ? { Channel: isMobile ? 'Mobile' : 'Web' } : {}),
        IsFromPlatform: isFromPlatform || journeyData.pegaStartingFields.IsFromPlatform,
      },
      // use last 'l' param to match old demo onboarding behavior;
      // fall back to config lang, then region default (eu1/eu2), then en
      lang:
        query.get('language') ||
        query.getAll('l').pop() ||
        journeyData.lang ||
        getDefaultLanguageByRegion(queryRegion) ||
        'en',
      region: resolvedRegion,
      isRegionUnknown: !queryRegion && !journeyData.region,
      default_platform: journeyData.default_platform,
      multiasset: query.get('isMultiAsset') === 'true' || journeyData.pegaStartingFields.MultiAsset,
    };
  }, [journeyData, journeyId, query, isMobile]);

  return <MergedConfigContext.Provider value={mergedConfig}>{children}</MergedConfigContext.Provider>;
}

function useMergedConfig(): MergedConfig {
  const context = React.useContext(MergedConfigContext);
  if (!context) {
    throw new Error('useMergedConfig must be used within a MergedConfigProvider');
  }
  return context;
}

export { MergedConfigProvider, useMergedConfig };
